(() => {
  if (!window.customElements || window.customElements.get('newly-added-gallery')) return;

  const FOCUSABLE = 'a[href],area[href],button,input,select,textarea,[tabindex],summary,[contenteditable],iframe,audio[controls],video[controls]';
  const REFERENCES = ['for', 'form', 'list', 'headers', 'aria-controls', 'aria-labelledby', 'aria-describedby', 'aria-owns', 'aria-activedescendant', 'aria-details', 'aria-errormessage', 'aria-flowto'];
  let instance = 0;

  class NewlyAddedGallery extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._viewport = this.querySelector('.newly-added__viewport');
      this._track = this.querySelector('.newly-added__track');
      this._group = this.querySelector('.newly-added__group:not([data-gallery-clone])');
      if (!this._viewport || !this._track || !this._group) return;
      this._connected = true;
      this._suffix ||= `gallery-copy-${Date.now().toString(36)}-${++instance}`;
      this._listeners = [];
      this._pointers = new Set();
      this._drawers = new Set(this._group.querySelectorAll('quick-buy-drawer'));
      this._motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this._touchPointer = window.matchMedia('(hover: none) and (pointer: coarse)');
      this._manualPaused ??= false;
      this._hovered = false;
      this._interacting = false;
      this._keyboard = true;
      this._visible = !window.IntersectionObserver;
      this._position = this._viewport.scrollLeft;
      this._speed = Math.max(0, Math.min(60, Number(this.dataset.speed) || 18));
      this._toggle = this.querySelector('[data-gallery-toggle]');

      this._listen(this._viewport, 'pointerenter', event => {
        if (event.pointerType === 'touch') return;
        this._hovered = true;
        this._syncMotion();
      });
      this._listen(this._viewport, 'pointerleave', event => {
        if (event.pointerType === 'touch') return;
        this._hovered = false;
        this._syncMotion();
      });
      this._listen(this._viewport, 'pointerdown', event => {
        this._keyboard = false;
        this._pointers.add(event.pointerId);
        this._hold();
        // Copies remain clickable, but mouse clicks must not focus hidden duplicates.
        if (event.pointerType === 'mouse' && this._clones?.some(clone => clone.contains(event.target)) && event.target.closest(FOCUSABLE)) {
          event.preventDefault();
        }
      });
      const release = event => {
        if (this._pointers.delete(event.pointerId)) this._hold();
      };
      this._listen(document, 'pointerup', release);
      this._listen(document, 'pointercancel', release);
      this._listen(this._viewport, 'wheel', () => this._hold(), { passive: true });
      this._listen(this._viewport, 'scroll', () => this._onScroll(), { passive: true });
      this._listen(document, 'keydown', event => {
        if (event.key === 'Tab') this._keyboard = true;
        if (this._viewport.contains(event.target)) {
          this._keyboard = true;
          this._syncMotion();
        }
      });
      this._listen(this, 'focusin', event => {
        const original = this._cloneFocus?.get(event.target);
        if (original) {
          original.focus({ preventScroll: true });
          original.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'instant' });
        }
        this._syncMotion();
      });
      this._listen(this, 'focusout', () => {
        queueMicrotask(() => { if (this._connected) this._syncMotion(); });
      });
      this._listen(document, 'visibilitychange', () => {
        if (document.hidden) this._pointers.clear();
        this._syncMotion();
      });
      this._listen(document, 'toggle', event => {
        if (this._drawers.has(event.target)) this._syncMotion();
      });
      this._listen(window, 'blur', () => {
        this._pointers.clear();
        this._hovered = false;
        this._hold();
      });
      this._listen(window, 'resize', () => this._scheduleMeasure(), { passive: true });
      if (this._toggle) this._listen(this._toggle, 'click', () => {
        this._manualPaused = !this._manualPaused;
        this._syncMotion();
      });
      const motionChanged = () => this._measure();
      if (this._motion.addEventListener) {
        this._listen(this._motion, 'change', motionChanged);
      } else {
        this._motion.addListener(motionChanged);
        this._listeners.push(() => this._motion.removeListener(motionChanged));
      }
      if (window.ResizeObserver) {
        this._resize = new window.ResizeObserver(() => this._scheduleMeasure());
        this._resize.observe(this._viewport);
        this._resize.observe(this._group);
      }
      if (window.IntersectionObserver) {
        this._intersection = new window.IntersectionObserver(entries => {
          this._visible = entries.some(entry => entry.target === this && entry.isIntersecting);
          this._syncMotion();
        });
        this._intersection.observe(this);
      }
      this._measure();
      this.setAttribute('data-ready', '');
    }

    disconnectedCallback() {
      this._connected = false;
      this._stop();
      window.cancelAnimationFrame(this._measureFrame);
      this._measureFrame = null;
      window.clearTimeout(this._settleTimer);
      this._listeners?.forEach(remove => remove());
      this._listeners = [];
      this._resize?.disconnect();
      this._intersection?.disconnect();
      this._removeClone();
      this._measuredWidth = null;
      this.removeAttribute('data-ready');
      this.removeAttribute('data-running');
    }

    _listen(target, type, listener, options) {
      target.addEventListener(type, listener, options);
      this._listeners.push(() => target.removeEventListener(type, listener, options));
    }

    _scheduleMeasure() {
      if (this._measureFrame || !this._connected) return;
      this._measureFrame = window.requestAnimationFrame(() => {
        this._measureFrame = null;
        this._measure();
      });
    }

    _measure() {
      if (!this._connected) return;
      const cycle = this._group.getBoundingClientRect().width;
      const width = this._viewport.clientWidth;
      // Mobile browser chrome changes viewport height without changing this rail.
      if (cycle === this._cycle && width === this._measuredWidth && this._motion.matches === this._measuredMotion) return;
      this._stop();
      // The group includes one trailing card gap; the track itself has no gap.
      this._cycle = cycle;
      this._measuredWidth = width;
      this._measuredMotion = this._motion.matches;
      const repeatShort = this.hasAttribute('data-repeat-short');
      this._enabled = !this._motion.matches && window.getComputedStyle(this._viewport).direction !== 'rtl' && width > 0 && this._cycle > 0 && (repeatShort || this._cycle > width + 1);
      this._copyCount = repeatShort && this._enabled ? Math.max(1, Math.ceil(width / this._cycle)) : 1;
      if (this._clone && this._clones.length !== this._copyCount) this._removeClone();
      if (this._enabled && !this._clone) this._makeClone();
      if (!this._enabled && this._clone) this._removeClone();
      this._position = this._viewport.scrollLeft;
      this.toggleAttribute('data-static', !this._enabled);
      this._syncMotion();
    }

    _makeClone() {
      this._clones = [];
      this._cloneFocus = new WeakMap();
      for (let copy = 0; copy < this._copyCount; copy++) this._makeCloneCopy(copy);
    }

    _makeCloneCopy(copy) {
      // A template keeps theme custom elements inert until their IDs are repaired.
      const template = document.createElement('template');
      template.innerHTML = this._group.outerHTML;
      const clone = template.content.firstElementChild;
      clone.setAttribute('data-gallery-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      const originalFocus = this._group.querySelectorAll(FOCUSABLE);
      clone.querySelectorAll(FOCUSABLE).forEach((node, index) => {
        if (originalFocus[index]) this._cloneFocus.set(node, originalFocus[index]);
        node.setAttribute('tabindex', '-1');
      });
      // Shared original drawers retain the theme's modal and focus behavior.
      clone.querySelectorAll('quick-buy-drawer').forEach(drawer => drawer.remove());
      const ids = new Map();
      [clone, ...clone.querySelectorAll('[id]')].forEach(node => {
        if (!node.id) return;
        const id = `${node.id}-${this._suffix}-${copy}`;
        ids.set(node.id, id);
        node.id = id;
      });
      [clone, ...clone.querySelectorAll('*')].forEach(node => {
        for (const attribute of REFERENCES) {
          if (!node.hasAttribute(attribute)) continue;
          node.setAttribute(attribute, node.getAttribute(attribute).split(/\s+/).map(id => ids.get(id) || id).join(' '));
        }
        for (const attribute of ['href', 'xlink:href']) {
          const value = node.getAttribute(attribute);
          if (value?.startsWith('#') && ids.has(value.slice(1))) node.setAttribute(attribute, `#${ids.get(value.slice(1))}`);
        }
        if (node.matches('input[type="radio"][name]') && (!node.closest('form') || node.name.startsWith('swatch-'))) {
          node.name = `${node.name}-${this._suffix}-${copy}`;
        }
        node.removeAttribute('reveal-js');
      });
      this._clone ||= clone;
      this._clones.push(clone);
      this._track.append(clone);
    }

    _removeClone() {
      if (!this._clone) return;
      if (this._cycle > 0 && this._viewport.scrollLeft >= this._cycle) {
        this._viewport.scrollLeft %= this._cycle;
      }
      this._clones.forEach(clone => clone.remove());
      this._clones = [];
      this._clone = null;
      this._cloneFocus = null;
    }

    _hold() {
      if (!this._connected) return;
      this._stop();
      this._interacting = true;
      this._position = this._viewport.scrollLeft;
      this._expectedScroll = this._position;
      this._syncMotion();
      window.clearTimeout(this._settleTimer);
      this._settleTimer = window.setTimeout(() => {
        if (!this._connected || this._pointers.size) return;
        this._interacting = false;
        this._position = this._viewport.scrollLeft;
        this._syncMotion();
      }, 1000);
    }

    _onScroll() {
      const actual = this._viewport.scrollLeft;
      // Browser scroll positions may be rounded. Retain the fractional accumulator.
      if (this._expectedScroll != null && Math.abs(actual - this._expectedScroll) <= 1) return;
      this._hold();
    }

    _canRun() {
      const active = document.activeElement;
      const focused = this._viewport.contains(active) && (active !== this._viewport || this._keyboard);
      const drawerOpen = [...this._drawers].some(drawer => drawer.hasAttribute('open'));
      return this._connected && this._enabled && this._visible && !document.hidden && !this._motion.matches && !this._manualPaused && !this._hovered && !this._interacting && !this._pointers.size && !focused && !drawerOpen;
    }

    _syncMotion() {
      if (!this._connected) return;
      if (this._toggle) {
        this._toggle.hidden = !this._enabled;
        this._toggle.disabled = !this._enabled;
        this._toggle.setAttribute('aria-pressed', String(this._manualPaused));
        const label = this._manualPaused ? 'Resume motion' : 'Pause motion';
        this._toggle.setAttribute('aria-label', label);
        (this._toggle.querySelector('[data-gallery-toggle-label]') || this._toggle).textContent = label;
      }
      if (!this._canRun()) {
        this._stop();
        return;
      }
      if (this._frame) return;
      this._position = this._viewport.scrollLeft;
      this._scrollOrigin = this._position;
      this._lastTime = null;
      this._velocity = 0;
      this.setAttribute('data-running', '');
      this._frame = window.requestAnimationFrame(time => this._tick(time));
    }

    _stop() {
      window.cancelAnimationFrame(this._frame);
      // Hand the visual position back to native scrolling before touch/keyboard use.
      if (this.hasAttribute('data-running')) {
        this._track.style.removeProperty('transform');
        this._viewport.scrollLeft = this._position;
        this._expectedScroll = this._viewport.scrollLeft;
      }
      this._frame = null;
      this._lastTime = null;
      this._velocity = 0;
      this.removeAttribute('data-running');
    }

    _tick(time) {
      this._frame = null;
      if (!this._canRun()) {
        this._stop();
        return;
      }
      const elapsed = this._lastTime == null ? 0 : Math.min((time - this._lastTime) / 1000, 0.05);
      this._lastTime = time;
      const speed = this._speed * (this._touchPointer.matches ? 1.5 : 1);
      this._velocity += (speed - this._velocity) * (1 - Math.exp(-elapsed / 0.45));
      this._position = (this._position + this._velocity * elapsed) % this._cycle;
      this._track.style.transform = `translate3d(${this._scrollOrigin - this._position}px, 0, 0)`;
      this._frame = window.requestAnimationFrame(nextTime => this._tick(nextTime));
    }
  }

  window.customElements.define('newly-added-gallery', NewlyAddedGallery);
})();
